import { useRef, useCallback } from 'react';
import * as mediasoupClient from 'mediasoup-client';
import { MAX_RECONNECT_ATTEMPTS, HEALTH_CHECK_INTERVAL } from '../utils/constants';

// Every signalling ack gets a deadline: one dropped on a disconnect must not leave an await
// (and the recoverAll guard) hanging for the rest of the page session.
const ACK_TIMEOUT_MS = 10000;

export function useMediaSoup({ socket, onTrackUpdate, onUnitUpdate }) {
  const device = useRef(null);
  const deviceReady = useRef(false);
  const sendTransport = useRef(null);
  // One recv transport carries every unit's consumers. The server keeps a single
  // receive transport per socket and closes the previous one when a new one is created.
  const recvTransport = useRef(null);
  const recvTransportPromise = useRef(null);
  const recoverAllRef = useRef(null);
  const recoveringAll = useRef(false);
  // Bumped by resetDevice so work started before a reset (disconnect/logout) knows it is stale
  const resetEpoch = useRef(0);
  const consumers = useRef(new Map());
  const producers = useRef(new Map());
  const pendingConsumptions = useRef([]);
  const healthCheckTimers = useRef(new Map());
  const reconnectAttempts = useRef(new Map());
  const lastTrackAliveRef = useRef(new Map());

  const isDeviceReady = useCallback(() => deviceReady.current && device.current, []);
  const getDevice = useCallback(() => device.current, []);

  const initDevice = useCallback(async (routerRtpCapabilities) => {
    device.current = new mediasoupClient.Device();
    await device.current.load({ routerRtpCapabilities });
    deviceReady.current = true;
    console.log('✅ Device loaded');
  }, []);

  // Server-side transports die with the socket, so drop every local recv transport/consumer;
  // the rejoin after reconnect ('available-units') consumes fresh ones. On logout
  // (forgetProducers) the producer list goes too, since the next login gets a fresh one.
  const resetDevice = useCallback((forgetProducers = false) => {
    resetEpoch.current += 1;
    deviceReady.current = false;
    pendingConsumptions.current = [];
    if (sendTransport.current) {
      sendTransport.current.close();
      sendTransport.current = null;
    }
    for (const [unitId, unitConsumers] of consumers.current.entries()) {
      for (const kind of ['video', 'audio']) {
        if (unitConsumers?.[kind]) {
          try { unitConsumers[kind].close(); } catch (err) { /* already closed */ }
          onTrackUpdate?.(unitId, kind, null);
        }
      }
    }
    try { recvTransport.current?.close(); } catch (err) { /* already closed */ }
    recvTransport.current = null;
    recvTransportPromise.current = null;
    recoveringAll.current = false;
    consumers.current.clear();
    reconnectAttempts.current.clear();
    if (forgetProducers) producers.current.clear();
  }, [onTrackUpdate]);

  const stopHealthCheck = useCallback((unitId) => {
    const timer = healthCheckTimers.current.get(unitId);
    if (timer) {
      clearInterval(timer);
      healthCheckTimers.current.delete(unitId);
      console.log(`🛑 Health check stopped for ${unitId}`);
    }
  }, []);

  const stopAllHealthChecks = useCallback(() => {
    for (const timer of healthCheckTimers.current.values()) {
      clearInterval(timer);
    }
    healthCheckTimers.current.clear();
    reconnectAttempts.current.clear();
  }, []);

  const requestKeyFrame = useCallback((unitId) => {
    const videoConsumer = consumers.current.get(unitId)?.video;
    if (videoConsumer?.requestKeyFrame) {
      videoConsumer.requestKeyFrame().catch((err) => {
        console.warn(`Failed to request keyframe for ${unitId}`, err);
      });
    }
  }, []);

  const closeConsumer = useCallback((unitId, kind) => {
    const unitConsumers = consumers.current.get(unitId);
    if (unitConsumers?.[kind]) {
      try {
        unitConsumers[kind].close();
        console.log(`🔒 Closed ${kind} consumer for ${unitId}`);
      } catch (err) {
        console.warn(`Failed to close ${kind} consumer for ${unitId}:`, err);
      }
      unitConsumers[kind] = null;
      consumers.current.set(unitId, unitConsumers);
    }
    onTrackUpdate?.(unitId, kind, null);
  }, [onTrackUpdate]);

  const createRecvTransport = useCallback(async () => {
    const epoch = resetEpoch.current;
    return new Promise((resolve, reject) => {
      socket.current.timeout(ACK_TIMEOUT_MS).emit('createTransport', { direction: 'receive' }, async (err, params) => {
        if (err) {
          reject(new Error(`createTransport: ${err.message}`));
          return;
        }
        if (params.error) {
          reject(new Error(params.error));
          return;
        }
        if (epoch !== resetEpoch.current) {
          reject(new Error('Receive transport answer arrived after a reset'));
          return;
        }

        try {
          if (!device.current) {
            reject(new Error('Device not ready'));
            return;
          }

          const transport = device.current.createRecvTransport(params);

          transport.on('connect', async ({ dtlsParameters }, callback, errback) => {
            try {
              socket.current.timeout(ACK_TIMEOUT_MS).emit('connectTransport', {
                direction: 'receive',
                dtlsParameters
              }, (ackErr, result) => {
                if (ackErr) errback(new Error(`connectTransport: ${ackErr.message}`));
                else if (result.error) errback(new Error(result.error));
                else callback();
              });
            } catch (error) {
              errback(error);
            }
          });

          transport.on('connectionstatechange', (state) => {
            if (state === 'failed') {
              console.warn('⚠️ Recv transport failed - rebuilding');
              setTimeout(() => recoverAllRef.current?.(transport), 1000);
            } else if (state === 'disconnected') {
              // ICE often recovers by itself from 'disconnected'; rebuild only if it stays down.
              setTimeout(() => {
                if (transport.connectionState === 'disconnected') {
                  console.warn('⚠️ Recv transport still disconnected - rebuilding');
                  recoverAllRef.current?.(transport);
                }
              }, 5000);
            } else if (state === 'connected') {
              reconnectAttempts.current.clear();
            }
          });

          recvTransport.current = transport;
          resolve(transport);
        } catch (error) {
          reject(error);
        }
      });
    });
  }, [socket]);

  const getRecvTransport = useCallback(async () => {
    const current = recvTransport.current;
    if (current && !current.closed && current.connectionState !== 'failed') return current;
    // A failed transport still carries the other units' consumers, and creating a new one makes
    // the server close it: leave the swap to recoverAll, which re-consumes every unit.
    if (current && !current.closed) throw new Error('Receive transport failed; waiting for recovery');
    if (!recvTransportPromise.current) {
      const promise = createRecvTransport().finally(() => {
        // resetDevice may already have replaced this promise; don't clear the newer one
        if (recvTransportPromise.current === promise) recvTransportPromise.current = null;
      });
      recvTransportPromise.current = promise;
    }
    return recvTransportPromise.current;
  }, [createRecvTransport]);

  const consumeMedia = useCallback(async (unitId, kind, producerId) => {
    try {
      if (!producerId) {
        const key = `${unitId}-${kind}`;
        producerId = producers.current.get(key);
        if (!producerId) {
          console.warn(`⚠️ No producer found for ${key}`);
          return null;
        }
      }

      if (!deviceReady.current || !device.current?.rtpCapabilities) {
        const alreadyQueued = pendingConsumptions.current.some(
          (item) => item.unitId === unitId && item.kind === kind
        );
        if (!alreadyQueued) {
          pendingConsumptions.current.push({ unitId, kind, producerId });
        }
        console.warn(`⏳ Device not ready. Queued ${kind} for ${unitId}`);
        return null;
      }

      if (!socket.current?.connected) {
        console.warn(`⚠️ Socket not ready. Skipping ${kind} for ${unitId}`);
        return null;
      }

      // The shared transport failed: rebuild it with every unit's consumers (this producer is
      // stored, so recoverAll consumes it too) rather than stranding the others on it.
      const current = recvTransport.current;
      if (current && !current.closed && current.connectionState === 'failed') {
        console.warn(`⚠️ Recv transport failed - rebuilding before consuming ${kind} for ${unitId}`);
        recoverAllRef.current?.(current);
        return null;
      }

      const transport = await getRecvTransport();

      return new Promise((resolve, reject) => {
        socket.current.timeout(ACK_TIMEOUT_MS).emit('consume', {
          producerId,
          rtpCapabilities: device.current.rtpCapabilities
        }, async (err, params) => {
          if (err) {
            reject(new Error(`consume ${kind} for ${unitId}: ${err.message}`));
            return;
          }
          if (params.error) {
            console.warn(`⚠️ Cannot consume ${kind} for ${unitId}:`, params.error);
            resolve(null);
            return;
          }

          try {
            const consumer = await transport.consume({
              id: params.id,
              producerId: params.producerId,
              kind: params.kind,
              rtpParameters: params.rtpParameters,
            });

            consumer.track.onended = () => {
              console.warn(`⚠️ Track ended for ${unitId} ${kind}`);
              onTrackUpdate?.(unitId, kind, null);
            };

            consumer.track.onmute = () => console.warn(`🔇 Track muted for ${unitId} ${kind}`);
            consumer.track.onunmute = () => console.log(`🔊 Track unmuted for ${unitId} ${kind}`);

            const unitConsumers = consumers.current.get(unitId) || {};
            unitConsumers[kind] = consumer;
            consumers.current.set(unitId, unitConsumers);

            if (kind === 'video') {
              lastTrackAliveRef.current.set(unitId, Date.now());
            }

            onTrackUpdate?.(unitId, kind, consumer.track);
            startHealthCheck(unitId);

            resolve(consumer);
          } catch (error) {
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error(`❌ Error consuming ${kind} for ${unitId}:`, error);
      throw error;
    }
  }, [socket, getRecvTransport, onTrackUpdate]);

  // The shared recv transport itself died: drop every consumer, build a new transport and
  // consume every known producer again.
  const recoverAll = useCallback(async (sourceTransport) => {
    if (!deviceReady.current || !socket.current?.connected) return;
    if (sourceTransport && recvTransport.current !== sourceTransport) return;
    if (recoveringAll.current) return;
    recoveringAll.current = true;
    const epoch = resetEpoch.current;
    try {
      for (const [unitId, unitConsumers] of consumers.current.entries()) {
        for (const kind of ['video', 'audio']) {
          if (unitConsumers?.[kind]) {
            try { unitConsumers[kind].close(); } catch (err) { /* already closed */ }
            onTrackUpdate?.(unitId, kind, null);
          }
        }
      }
      consumers.current.clear();
      try { recvTransport.current?.close(); } catch (err) { /* already closed */ }
      recvTransport.current = null;

      for (const key of producers.current.keys()) {
        // A reset (disconnect/logout) happened meanwhile: the rejoin consumes everything afresh
        if (epoch !== resetEpoch.current) break;
        const split = key.lastIndexOf('-');
        const unitId = key.slice(0, split);
        const kind = key.slice(split + 1);
        try {
          await consumeMedia(unitId, kind, producers.current.get(key));
        } catch (error) {
          console.error(`❌ Re-consume ${kind} for ${unitId} failed:`, error);
        }
      }
    } finally {
      // After a reset the flag already belongs to the new session
      if (epoch === resetEpoch.current) {
        recoveringAll.current = false;
        // The rebuilt transport can fail while we are still busy; its own 'failed' retry was
        // skipped by the guard above, so retry from here.
        const rebuilt = recvTransport.current;
        if (rebuilt && !rebuilt.closed && rebuilt.connectionState === 'failed') {
          setTimeout(() => recoverAllRef.current?.(rebuilt), 1000);
        }
      }
    }
  }, [consumeMedia, onTrackUpdate, socket]);
  recoverAllRef.current = recoverAll;

  // One unit's track died: re-consume just that unit on the shared transport.
  const attemptReconnect = useCallback(async (unitId, sourceTransport) => {
    // While the socket is down the rejoin path re-consumes everything; retrying here would queue stale producers.
    if (!deviceReady.current || !socket.current?.connected) {
      return;
    }
    if (sourceTransport && recvTransport.current !== sourceTransport) {
      return;
    }

    const currentAttempts = reconnectAttempts.current.get(unitId) || 0;

    if (currentAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error(`❌ Max reconnect attempts reached for ${unitId}`);
      return;
    }

    console.log(`🔄 Reconnect attempt ${currentAttempts + 1}/${MAX_RECONNECT_ATTEMPTS} for ${unitId}`);
    reconnectAttempts.current.set(unitId, currentAttempts + 1);

    try {
      const unitConsumers = consumers.current.get(unitId);
      if (unitConsumers) {
        if (unitConsumers.video) unitConsumers.video.close();
        if (unitConsumers.audio) unitConsumers.audio.close();
      }
      consumers.current.delete(unitId);

      const videoProducerId = producers.current.get(`${unitId}-video`);
      const audioProducerId = producers.current.get(`${unitId}-audio`);

      if (videoProducerId) {
        await consumeMedia(unitId, 'video', videoProducerId);
      }
      if (audioProducerId) {
        await consumeMedia(unitId, 'audio', audioProducerId);
      }

      startHealthCheck(unitId);
    } catch (error) {
      console.error(`❌ Reconnect failed for ${unitId}:`, error);
      setTimeout(() => attemptReconnect(unitId), 5000);
    }
  }, [consumeMedia, socket]);

  const startHealthCheck = useCallback((unitId) => {
    const existingTimer = healthCheckTimers.current.get(unitId);
    if (existingTimer) clearInterval(existingTimer);

    const timer = setInterval(() => {
      const unitConsumers = consumers.current.get(unitId);
      if (!unitConsumers) return;

      const videoTrack = unitConsumers.video?.track;
      const audioTrack = unitConsumers.audio?.track;

      if (videoTrack?.readyState === 'ended' || audioTrack?.readyState === 'ended') {
        console.warn(`⚠️ Track ended for ${unitId} - attempting recovery`);
        clearInterval(timer);
        healthCheckTimers.current.delete(unitId);
        attemptReconnect(unitId);
        return;
      }

      requestKeyFrame(unitId);
    }, HEALTH_CHECK_INTERVAL);

    healthCheckTimers.current.set(unitId, timer);
  }, [attemptReconnect, requestKeyFrame]);

  const flushQueuedConsumptions = useCallback(async () => {
    if (!deviceReady.current || !device.current) return;
    if (!pendingConsumptions.current.length) return;

    const queue = [...pendingConsumptions.current];
    pendingConsumptions.current = [];

    for (const job of queue) {
      try {
        await consumeMedia(job.unitId, job.kind, job.producerId);
      } catch (error) {
        console.error(`Failed to process queued ${job.kind} for ${job.unitId}:`, error);
      }
    }
  }, [consumeMedia]);

  const storeProducer = useCallback((unitId, kind, producerId) => {
    producers.current.set(`${unitId}-${kind}`, producerId);
  }, []);

  const getProducerId = useCallback((unitId, kind) => {
    return producers.current.get(`${unitId}-${kind}`);
  }, []);

  const removeProducer = useCallback((unitId, kind) => {
    producers.current.delete(`${unitId}-${kind}`);
  }, []);

  const cleanupUnit = useCallback((unitId) => {
    stopHealthCheck(unitId);
    consumers.current.delete(unitId);
    producers.current.delete(`${unitId}-video`);
    producers.current.delete(`${unitId}-audio`);
    reconnectAttempts.current.delete(unitId);
    lastTrackAliveRef.current.delete(unitId);
  }, [stopHealthCheck]);

  const getConsumer = useCallback((unitId, kind) => {
    return consumers.current.get(unitId)?.[kind];
  }, []);

  const pauseAudioForUnit = useCallback(async (unitId) => {
    const unitConsumers = consumers.current.get(unitId);
    if (unitConsumers?.audio) {
      try {
        // Just pause, don't close - so we can resume later
        await unitConsumers.audio.pause();
        console.log(`⏸️ Audio consumer paused for ${unitId}`);
      } catch (error) {
        console.warn(`Failed to pause audio for ${unitId}:`, error);
      }
    }
    // Don't remove the track from state, just mark audio as not playing
  }, []);

  const consumeAudioForUnit = useCallback(async (unitId) => {
    try {
      const unitConsumers = consumers.current.get(unitId);
      if (unitConsumers?.audio) {
        if (unitConsumers.audio.paused) {
          await unitConsumers.audio.resume();
          console.log(`▶️ Audio resumed for ${unitId}`);
        }
        onTrackUpdate?.(unitId, 'audio', unitConsumers.audio.track);
        return unitConsumers.audio;
      }

      const audioProducerId = producers.current.get(`${unitId}-audio`);
      if (audioProducerId) {
        console.log(`🔊 Consuming audio for selected unit: ${unitId}`);
        return await consumeMedia(unitId, 'audio', audioProducerId);
      }
    } catch (error) {
      console.error(`Failed to consume audio for ${unitId}:`, error);
    }
    return null;
  }, [consumeMedia, onTrackUpdate]);

  const getLastTrackAlive = useCallback((unitId) => {
    return lastTrackAliveRef.current.get(unitId) || 0;
  }, []);

  const setLastTrackAlive = useCallback((unitId, time) => {
    lastTrackAliveRef.current.set(unitId, time);
  }, []);

  // Get bandwidth stats from all receive transports
  const getBandwidthStats = useCallback(async () => {
    let totalBytesReceived = 0;

    const transport = recvTransport.current;
    if (transport && !transport.closed) {
      try {
        const stats = await transport.getStats();
        stats.forEach(report => {
          if (report.type === 'inbound-rtp') {
            totalBytesReceived += report.bytesReceived || 0;
          }
        });
      } catch (err) {
        // Ignore stats errors
      }
    }

    return { bytesReceived: totalBytesReceived };
  }, []);

  return {
    isDeviceReady,
    getDevice,
    initDevice,
    resetDevice,
    consumeMedia,
    consumeAudioForUnit,
    pauseAudioForUnit,
    closeConsumer,
    flushQueuedConsumptions,
    storeProducer,
    getProducerId,
    removeProducer,
    cleanupUnit,
    getConsumer,
    requestKeyFrame,
    attemptReconnect,
    stopHealthCheck,
    stopAllHealthChecks,
    getLastTrackAlive,
    setLastTrackAlive,
    getBandwidthStats,
    sendTransport,
    device,
  };
}
